/* ============================================================================
   SHIPPING PRESETS API CALLS
   ============================================================================ */

import type { TrackData } from "./TrackShipmentModal";

const API_URL = process.env.REACT_APP_API_URL;

/* -------------------------------- TYPES -------------------------------- */

export type PackageDetails = {
  length: number;
  width: number;
  height: number;
  weight: number;
  dimension_unit: string;
  weight_unit: string;
}; 

export type ShippingPreset = {
  _id: string;
  name: string;
  carrier: string;
  carrier_account_id?: string;
  packaging_type: string;
  package: PackageDetails;
  ship_from_warehouse_id?: string;
  is_global: boolean;
  status: string;
  created_by?: { id: string; name: string };
  updated_by?: { id: string; name: string };
  created_at: string;
  updated_at: string;
};

export type DatatableFilters = {
  scope?: "all" | "mine" | "global";
  status?: string;
  carrier?: string;
};

export type DatatableParams = {
  page: number;
  limit: number;
  search?: string;
  filters?: DatatableFilters;
  sort?: { field: string; order: "asc" | "desc" };
  fields?: string[];
};

export type DatatableResponse = {
  data: ShippingPreset[];
  limit: number;
  page: number;
  total: number;
  totalPages: number;
};

export type CreatePresetPayload = {
  name: string;
  carrier: string;
  carrier_account_id: string;
  packaging_type: string;
  package?: PackageDetails;
  ship_from_warehouse_id?: string;
  status: string;
  is_global?: boolean;
};

export type UpdatePresetPayload = Partial<CreatePresetPayload>;

export type ApiResponse<T> = {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
};

/* -------------------------------- API FUNCTIONS -------------------------------- */

/**
 * 1. Fetch Shipping Presets DataTable
 * POST /api/v1/shipping-presets/datatable
 */
export const fetchShippingPresetsDataTable = async (
  token: string,
  params: DatatableParams
): Promise<DatatableResponse> => {
  const response = await fetch(`${API_URL}/shipping-presets/datatable`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page: params.page,
      limit: params.limit,
      search: params.search || "",
      filters: params.filters || {},
      sort: params.sort || { field: "created_at", order: "desc" },
      fields: params.fields || [],
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to fetch shipping presets");
  }

  return response.json();
};

/**
 * 2. Create Shipping Preset (Custom/User)
 * POST /api/v1/shipping-presets
 */
export const createShippingPreset = async (
  token: string,
  payload: CreatePresetPayload
): Promise<ApiResponse<ShippingPreset>> => {
  const response = await fetch(`${API_URL}/shipping-presets`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  // Handle different response formats:
  // - { "success": true, ... } (wrapped success)
  // - HTTP 2xx status with any result
  if (result.success === true) {
    return result;
  }

  // If response is OK (2xx), treat as success even without explicit success flag
  if (response.ok) {
    return result;
  }

  // Only throw error if response is not OK
  throw new Error(result?.message || result?.error || "Failed to create preset");
};

/**
 * 3. Get Shipping Preset by ID
 * GET /api/v1/shipping-presets/:id
 */
export const getShippingPresetById = async (
  token: string,
  presetId: string
): Promise<ApiResponse<ShippingPreset>> => {
  const response = await fetch(`${API_URL}/shipping-presets/${presetId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result?.message || result?.error || "Failed to fetch preset");
  }

  return result;
};

/**
 * 4. Update Shipping Preset
 * PUT /api/v1/shipping-presets/:id
 */
export const updateShippingPreset = async (
  token: string,
  presetId: string,
  payload: UpdatePresetPayload
): Promise<ApiResponse<{ updated: boolean }>> => {
  const response = await fetch(`${API_URL}/shipping-presets/${presetId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  // Handle different response formats:
  // - { "updated": true } (direct success indicator)
  // - { "success": true, ... } (wrapped success)
  // - HTTP 2xx status with any result
  if (result.updated === true || result.success === true) {
    return result;
  }

  // If response is OK (2xx) but no success indicator, still treat as success
  if (response.ok) {
    return result;
  }

  // Only throw error if response is not OK
  throw new Error(result?.message || result?.error || "Failed to update preset");
};

/**
 * 5. Delete Shipping Preset
 * DELETE /api/v1/shipping-presets/:id
 */
export const deleteShippingPreset = async (
  token: string,
  presetId: string
): Promise<ApiResponse<{ deleted: boolean }>> => {
  const response = await fetch(`${API_URL}/shipping-presets/${presetId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  // API returns { "deleted": true } on success; some may return { "success": true }
  if (result.deleted === true || result.success === true) {
    return result;
  }
  if (response.ok) {
    return result;
  }

  throw new Error(result?.message || result?.error || "Failed to delete preset");
};

/**
 * 6. Create Global Preset (super_admin only)
 * POST /api/v1/shipping-presets with is_global: true
 */
export const createGlobalShippingPreset = async (
  token: string,
  payload: Omit<CreatePresetPayload, "is_global">
): Promise<ApiResponse<ShippingPreset>> => {
  return createShippingPreset(token, { ...payload, is_global: true });
};

/* ============================================================================
   POSTAGE WALLET API CALLS
   ============================================================================ */

export type WalletBalanceResponse = {
  wallet_id: string;
  currency: string;
  balance_cents: number;
  balance: string;
};

/**
 * Get Wallet Balance
 * GET /api/v1/postage-wallet/balance
 */
export const getWalletBalance = async (
  token: string
): Promise<WalletBalanceResponse> => {
  const response = await fetch(`${API_URL}/postage-wallet/balance?permissions=false`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch wallet balance");
  }

  // Handle both response formats (with or without success wrapper)
  return result.data || result;
};

export type TopupPaymentIntentResponse = {
  wallet_id: string;
  transaction_id: string;
  currency: string;
  amount_cents: number;
  amount: string;
  balance_cents: number;
  balance: string;
  stripe_payment_intent_id: string;
  client_secret: string;
  publishable_key: string;
};

/**
 * Create Payment Intent for Postage Wallet Topup
 * POST /api/v1/postage-wallet/topup/payment-intent
 */
export const createTopupPaymentIntent = async (
  token: string,
  amount: number
): Promise<TopupPaymentIntentResponse> => {
  const response = await fetch(`${API_URL}/postage-wallet/topup/payment-intent`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ amount }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to create payment intent");
  }

  // Handle both response formats (with or without success wrapper)
  return result.data || result;
};

/* -------------------------------- TRANSACTIONS -------------------------------- */

export type WalletTransaction = {
  _id: string;
  wallet_id: string;
  user_id: string;
  type: string;
  status: string;
  currency: string;
  amount_cents: number;
  starting_balance_cents: number;
  ending_balance_cents: number;
  amount: string;
  starting_balance: string;
  ending_balance: string;
  stripe_payment_intent_id?: string;
  stripe_event_id?: string;
  stripe_status?: string;
  stripe_latest_charge_id?: string;
  stripe_customer_id?: string;
  wallet_applied?: boolean;
  applied_at?: string;
  failure_reason?: string;
  created_at: string;
  updated_at: string;
};

export type TransactionDatatableParams = {
  page: number;
  limit: number;
  search?: string;
  filters?: { status?: string };
  sort?: { field: string; order: "asc" | "desc" };
  fields?: string[];
};

export type TransactionDatatableResponse = {
  data: WalletTransaction[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

/**
 * Fetch Wallet Transactions DataTable
 * POST /api/v1/postage-wallet/transactions/datatable
 */
export const fetchWalletTransactions = async (
  token: string,
  params: TransactionDatatableParams
): Promise<TransactionDatatableResponse> => {
  const response = await fetch(`${API_URL}/postage-wallet/transactions/datatable`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page: params.page,
      limit: params.limit,
      search: params.search || "",
      filters: params.filters || {},
      sort: params.sort || { field: "created_at", order: "desc" },
      fields: params.fields || [],
    }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch transactions");
  }

  // API returns { data: [...], total, page, limit, totalPages } directly
  return result;
};

/* ============================================================================
   SHIPPING ORDERS API CALLS
   ============================================================================ */

export type AddressDetails = {
  _id?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  cityCode?: string;
  country: string;
  countryCode?: string;
  label?: string;
  latitude?: number;
  longitude?: number;
  phone?: string;
  state: string;
  stateCode?: string;
  zip: string;
};

export type ShippingAddress = {
  _id: string;
  address_details: AddressDetails;
  company?: string;
  first_name: string;
  last_name: string;
  label?: string;
  user_id?: string;
  default_address?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type ShippingOrder = {
  id: string;
  order_number: string;
  customer_name: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  agent: string;
  office: string;
  product_name: string;
  product_sku: string;
  status: string;
  status_code: number;
  total: number;
  date: string;
  created_at: string;
  shipping_address: ShippingAddress;
  billing_address: ShippingAddress;
};

export type ShippingOrderSearchResponse = {
  pagination: {
    more: boolean;
  };
  results: ShippingOrder[];
};

/**
 * Search Shipping Orders
 * GET /api/v1/shipping/orders/search/:userId
 */
export const searchShippingOrders = async (
  token: string,
  userId: string,
  search?: string,
  page?: number
): Promise<ShippingOrderSearchResponse> => {
  let url = `${API_URL}/shipping/orders/search/${userId}`;
  
  // Add query params if provided
  const params = new URLSearchParams();
  params.set("permissions", "false");
  if (search) params.append("search", search);
  if (page) params.append("page", page.toString());
  
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to search orders");
  }

  return result;
};

/* ============================================================================
   CUSTOMER GROUPS (USERS CREATED OR LINKED) API CALLS
   ============================================================================ */

export type CustomerGroupUser = {
  id: string;
  name: string;
  email: string;
  /** From API response - use this (not current user's) when calling addresses/all/select2 */
  license_type?: string;
};

export type CustomerGroupUsersResponse = {
  results: CustomerGroupUser[];
  more: boolean;
  /** Total count from API when available (Select2 style) for page * limit < total */
  total?: number;
  limit?: number;
};

/**
 * Get users created or linked for customer-groups (paginated, searchable - Select2 style)
 * GET /api/v1/customer-groups/:userId/users-created-or-linked?query=&page=&limit=
 * Page-change logic matches SignageCreateOrder Select Agent dropdown.
 */
export const getCustomerGroupUsers = async (
  token: string,
  userId: string,
  query?: string,
  page?: number,
  limit?: number
): Promise<CustomerGroupUsersResponse> => {
  const pageNumber = page ?? 1;
  const limitNum = limit ?? 20;
  const q = query ?? "";
  const params = new URLSearchParams();
  params.set("query", q);
  params.set("page", String(pageNumber));
  params.set("limit", String(limitNum));
  const url = `${API_URL}/customer-groups/${encodeURIComponent(userId)}/users-created-or-linked?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const res = await response.json();

  if (!response.ok) {
    throw new Error(res?.message || res?.error || "Failed to fetch customer group users");
  }

  // Handle the new API response structure with data array
  const responseData = res.data?.data ?? res.data ?? (Array.isArray(res) ? res : []);
  const results: CustomerGroupUser[] = (Array.isArray(responseData) ? responseData : []).map(
    (u: any) => ({
      id: u.value ?? u.id ?? "",
      name: u.label?.split(" (")[0] ?? u.name ?? "",
      email: u.email ?? u.label?.match(/\((.*?)\)/)?.[1] ?? "",
      license_type: u.license_type,
    })
  );

  const total = res?.pagination?.total ?? res?.total;
  const apiLimit = res?.pagination?.limit ?? res?.limit ?? limitNum;
  // Select2 page-change logic (same as SignageCreateOrder Select Agent): prefer pagination.more, else page*limit < total, else full page
  const more =
    res?.pagination?.more ??
    (typeof total === "number" ? pageNumber * apiLimit < total : results.length >= limitNum);

  return { results, more, total: typeof total === "number" ? total : undefined, limit: limitNum };
};

/* ============================================================================
   ADDRESSES ALL SELECT2 API CALLS
   ============================================================================ */

export type AddressSelect2Item = {
  id: string;
  text: string;
};

export type AddressesAllSelect2Response = {
  results: AddressSelect2Item[];
  more: boolean;
  total?: number;
};

/**
 * Get addresses for a user (Select2 style) - id = selected user id, licenseType = current user license
 * GET /api/v1/addresses/all/select2?id=&licenseType=&search=&page=&limit=
 * UI shows each option as "text" (e.g. "Label — Street, City, State Zip").
 */
export const getAddressesAllSelect2 = async (
  token: string,
  id: string,
  licenseType: string,
  search?: string,
  page?: number,
  limit?: number
): Promise<AddressesAllSelect2Response> => {
  const pageNumber = page ?? 1;
  const limitNum = limit ?? 10;
  const params = new URLSearchParams();
  params.set("id", id);
  params.set("licenseType", licenseType);
  params.set("search", search ?? "");
  params.set("page", String(pageNumber));
  params.set("limit", String(limitNum));
  const url = `${API_URL}/addresses/all/select2?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const res = await response.json();

  if (!response.ok) {
    throw new Error(res?.message || res?.error || "Failed to fetch addresses");
  }

  const list = res?.results ?? (Array.isArray(res) ? res : []);
  const results: AddressSelect2Item[] = (Array.isArray(list) ? list : []).map((item: any) => ({
    id: item?.id ?? item?.value ?? "",
    text: item?.text ?? item?.label ?? "",
  }));

  const total = res?.pagination?.total ?? res?.total;
  const apiLimit = res?.pagination?.limit ?? res?.limit ?? limitNum;
  const more =
    res?.pagination?.more ??
    (typeof total === "number" ? pageNumber * apiLimit < total : results.length >= limitNum);

  return { results, more, total: typeof total === "number" ? total : undefined };
};

/**
 * Address by ID response - used to fill To Address fields when user selects an address
 */
export type AddressByIdResponse = {
  _id?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  label?: string;
  email?: string;
  address_details?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    stateCode?: string;
    country?: string;
    countryCode?: string;
    zip?: string;
    phone?: string;
  };
};

/**
 * Get address by ID - fill To Address fields when user selects address from dropdown
 * GET /api/v1/addresses/:id
 */
export const getAddressById = async (
  token: string,
  addressId: string
): Promise<AddressByIdResponse> => {
  const response = await fetch(`${API_URL}/addresses/${encodeURIComponent(addressId)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const res = await response.json();

  if (!response.ok) {
    throw new Error(res?.message || res?.error || "Failed to fetch address");
  }

  return res?.data ?? res;
};

/* ============================================================================
   EASYPOST CARRIER ACCOUNTS API CALLS
   ============================================================================ */

export type CarrierAccount = {
  _id: string;
  shipping_integration_id?: string; // Optional as it may not be in response
  carrier_name: string;
  carrier_account_id: string;
  status: string;
  created_by?: { id: string; name: string };
  updated_by?: { id: string; name: string };
  created_at: string;
  updated_at: string;
};

export type PackageDimension = {
  length: number;
  width: number;
  height: number;
  weight: number;
};

export type PredefinePackage = {
  carrier_name: string;
  package: string;
  dimensions: PackageDimension;
};

export type CustomPackage = {
  carrier_name: string;
  package: string;
  dimensions: PackageDimension;
};

export type CarrierPackageDetails = {
  _id: string;
  shipping_integration_id: string;
  carrier_name: string;
  carrier_account_id: string;
  status: string;
  predefine_packages: PredefinePackage[];
  custom_packages: CustomPackage[];
  created_by?: { id: string; name: string };
  updated_by?: { id: string; name: string };
  created_at: string;
  updated_at: string;
};

/**
 * Get Carrier Accounts
 * GET /api/v1/easypost/carrier-accounts
 * Note: shipping_integration_id is now optional in query params
 */
export const getCarrierAccounts = async (
  token: string,
  shipping_integration_id?: string
): Promise<CarrierAccount[]> => {
  let url = `${API_URL}/easypost/carrier-accounts`;
  
  // Add shipping_integration_id as query param only if provided
  if (shipping_integration_id) {
    const params = new URLSearchParams({
      shipping_integration_id: shipping_integration_id,
    });
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch carrier accounts");
  }

  // Handle both array response and wrapped response
  return Array.isArray(result) ? result : result.data || [];
};

/**
 * Get Carrier Package Details (Select2)
 * GET /api/v1/shipping-presets/select2?carrier=UPS&q=box&page=1&limit=10
 */
export type ShippingPresetSelect2Result = {
  id: string;
  text: string;
  name?: string;
  carrier?: string;
  packaging_type?: string;
  package?: {
    length: number;
    width: number;
    height: number;
    weight: number;
    dimension_unit?: string;
    weight_unit?: string;
  };
  ship_from_warehouse_id?: string | null;
  is_global?: boolean;
  status?: string;
};

export type ShippingPresetSelect2Response = {
  success: boolean;
  data: {
    results: ShippingPresetSelect2Result[];
    pagination: { more: boolean };
  };
};

export const getCarrierPackageDetails = async (
  token: string,
  carrier: string,
  q: string,
  page: number,
  limit: number
): Promise<{ results: ShippingPresetSelect2Result[]; more: boolean }> => {
  const params = new URLSearchParams();
  if (carrier) params.set("carrier", carrier);
  params.set("permissions", "false");
  params.set("q", q || "");
  params.set("page", String(page || 1));
  params.set("limit", String(limit || 10));

  const response = await fetch(`${API_URL}/shipping-presets/select2?${params.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch shipping presets");
  }

  // Support both { data: { results, pagination } } and top-level { results, pagination }
  const data = (result?.data ?? result) as any;
  const results: ShippingPresetSelect2Result[] = Array.isArray(data?.results) ? data.results : [];
  const more = !!data?.pagination?.more;
  return { results, more };
};

export type PackageTemplateSelect2Result = {
  id: string;
  text: string;
  name: string;
  carrier_id?: string;
  carrier_name: string;
  dimensions: {
    length: number;
    width: number;
    height: number;
    weight: number;
  };
  status?: string;
};

export type PackageTemplateSelect2Response = {
  success: boolean;
  status?: number;
  data: {
    results: PackageTemplateSelect2Result[];
    pagination?: { more: boolean };
    total?: number;
    page?: number;
  };
};

/**
 * Get Package Templates for Select2 (ALL USERS - No Admin Required)
 * GET /api/v1/package-templates/select2?q=&page=&limit=&status=&carrier=
 */
export const getPackageTemplatesSelect2 = async (
  token: string,
  params: {
    q?: string;
    page?: number;
    limit?: number;
    status?: string;
    carrier?: string;
  }
): Promise<{ results: PackageTemplateSelect2Result[]; more: boolean }> => {
  const qs = new URLSearchParams();
  if (params.q != null) qs.set("q", params.q);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.status) qs.set("status", params.status);
  if (params.carrier) qs.set("carrier", params.carrier);

  const response = await fetch(`${API_URL}/package-templates/select2?${qs.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = (await response.json().catch(() => null)) as PackageTemplateSelect2Response | any;
  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch package templates");
  }

  const data = (result?.data ?? result) as any;
  const results: PackageTemplateSelect2Result[] = Array.isArray(data?.results) ? data.results : [];
  const more = !!data?.pagination?.more;
  return { results, more };
};

/* ============================================================================
   PACKAGE TEMPLATES CRUD API CALLS (ADMIN ONLY)
   ============================================================================ */

export type PackageTemplate = {
  id: string;
  name: string;
  carrier_id: string;
  carrier_name: string;
  package_dimensions: {
    length: number;
    width: number;
    height: number;
    weight: number;
  };
  status: string;
  created_by?: { id: string; name: string; email?: string };
  updated_by?: { id: string; name: string; email?: string };
  created_at: string;
  updated_at: string;
};

export type PackageTemplateCreatePayload = {
  name: string;
  carrier_id: string;
  carrier_name: string;
  package_dimensions: {
    length: number;
    width: number;
    height: number;
    weight: number;
  };
  status: string;
};

export type PackageTemplateUpdatePayload = Partial<PackageTemplateCreatePayload>;

export type PackageTemplateDatatableParams = {
  draw?: number;
  start?: number;
  length?: number;
  search?: { value?: string };
  order?: Array<{ column: number; dir: "asc" | "desc" }>;
  columns?: Array<{ data: string }>;
  page?: number;
  sortBy?: string;
  sortDir?: -1 | 1;
  query?: {
    status?: string;
    carrier?: string;
  };
};

export type PackageTemplateDatatableResponse = {
  draw?: number;
  recordsTotal?: number;
  recordsFiltered?: number;
  data: PackageTemplate[];
  total?: number;
  limit?: number;
  page?: number;
};

/**
 * Create Package Template
 * POST /api/v1/package-templates
 */
export const createPackageTemplate = async (
  token: string,
  payload: PackageTemplateCreatePayload
): Promise<ApiResponse<PackageTemplate>> => {
  const response = await fetch(`${API_URL}/package-templates`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to create package template");
  }

  const data = result?.data ?? result;
  return { success: true, data };
};

/**
 * Get Package Templates DataTable
 * POST /api/v1/package-templates/datatable
 */
export const fetchPackageTemplatesDataTable = async (
  token: string,
  params: PackageTemplateDatatableParams
): Promise<PackageTemplateDatatableResponse> => {
  const response = await fetch(`${API_URL}/package-templates/datatable`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch package templates");
  }

  const payload = result?.success && result?.data != null ? result.data : result;
  return payload;
};

/**
 * Get Package Template by ID
 * GET /api/v1/package-templates/:id
 * Backend may return { success, data } or the template object directly.
 */
export const getPackageTemplateById = async (
  token: string,
  id: string
): Promise<ApiResponse<PackageTemplate>> => {
  const response = await fetch(`${API_URL}/package-templates/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.error || result?.message || "Failed to fetch package template");
  }

  const template = result?.data ?? result;
  return { success: true, data: template };
};

/**
 * Update Package Template
 * PUT /api/v1/package-templates/:id
 */
export const updatePackageTemplate = async (
  token: string,
  id: string,
  payload: PackageTemplateUpdatePayload
): Promise<ApiResponse<PackageTemplate>> => {
  const response = await fetch(`${API_URL}/package-templates/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to update package template");
  }

  const data = result?.data ?? result;
  return { success: true, data };
};

/**
 * Delete Package Template
 * DELETE /api/v1/package-templates/:id
 */
export const deletePackageTemplate = async (
  token: string,
  id: string
): Promise<ApiResponse<{ message: string }>> => {
  const response = await fetch(`${API_URL}/package-templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result?.message || result?.error || "Failed to delete package template");
  }

  return result;
};

/* ============================================================================
   EASYPOST RATES AND BUY LABEL API CALLS
   ============================================================================ */

export type AddressPayload = {
  name: string;
  company?: string;
  phone: string;
  email: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  city_code?: string;
  state: string;
  state_code: string;
  country: string;
  country_code: string;
  postal_code: string;
};

export type ParcelPayload = {
  length: number;
  width: number;
  height: number;
  weight: number;
  weight_unit?: string;
};

export type Rate = {
  id: string;
  carrier: string;
  service: string;
  rate: string;
  currency: string;
  est_delivery_days?: number;
  insurance_value?: string;
  insurance_fee_estimate?: string;
  total_rate_estimate?: string;
};

export type GetRatesPayload = {
  shipping_integration_id?: string;
  carrier_account_id: string;
  ship_without_address_validation: boolean;
  label_format: string;
  label_layout: string;
  insurance?: string;
  from_address: AddressPayload;
  to_address: AddressPayload;
  parcel: ParcelPayload;
};

export type GetRatesResponse = {
  shipment_id: string;
  rates: Rate[];
  address_verification?: {
    enabled: boolean;
    ship_without_address_validation: boolean;
    from?: { verified: boolean; message?: string };
    to?: { verified: boolean; message?: string };
  };
  label_preferences?: {
    label_format: string;
    label_layout: string;
  };
};

export type BuyLabelPayload = {
  // shipping_integration_id: string;
  shipment_id: string;
  rate_id: string;
  request_uuid: string;
  ship_without_address_validation: boolean;
  // label_format: string;
  // label_layout: string;
  insurance: string;
  order_database_id?: string;
  order_id?: string;
};

export type BuyLabelResponse = {
  _id: string;
  shipping_integration_id: string;
  request_uuid: string;
  shipment_id: string;
  rate_id: string;
  carrier: string;
  service: string;
  postage_amount: string;
  currency: string;
  tracking_code: string;
  label_url: string;
  label_format: string;
  label_layout: string;
  insurance: string;
  wallet_transaction_id: string;
  wallet_deduction?: {
    amount_cents: number;
    currency: string;
    starting_balance_cents: number;
    ending_balance_cents: number;
    status: string;
  };
  created_by?: { id: string; name: string };
  updated_by?: { id: string; name: string };
  created_at: string;
  updated_at: string;
};

/**
 * Get Shipping Rates
 * POST /api/v1/easypost/rates
 */
export const getShippingRates = async (
  token: string,
  payload: GetRatesPayload
): Promise<GetRatesResponse> => {
  const response = await fetch(`${API_URL}/shipping/rates`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch shipping rates");
  }

  return result.data || result;
};

/**
 * Buy Shipping Label
 * POST /api/v1/easypost/buy-label
 */
export const buyShippingLabel = async (
  token: string,
  payload: BuyLabelPayload
): Promise<BuyLabelResponse> => {
  const response = await fetch(`${API_URL}/shipping/buy-label`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to buy shipping label");
  }

  return result.data || result;
};

/**
 * Mark Order as Shipped
 * POST /api/v1/orders/:orderId/shipped
 */
export const markOrderAsShipped = async (
  token: string,
  orderId: string
): Promise<{ success: boolean; message?: string }> => {
  const response = await fetch(`${API_URL}/orders/${orderId}/shipped?permissions=false`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to mark order as shipped");
  }

  return result.data || result;
};

/** Minimal order shape used by shipped email flows. */
export type ShippingOrderDetail = {
  id: string;
  _id?: string;
  order_number?: string;
  signage_company_name?: string;
  inventory_source?: string;
  agent_id?: string;
  team_member_id?: string;
  brokerage_firm_id?: string;
  customer_email?: string;
  shipping_address?: {
    address_details?: {
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      zip?: string;
    };
  };
  items?: Array<{ product_name?: string; name?: string; quantity?: number }>;
};

/** GET /orders/:orderId */
export const getOrderById = async (
  token: string,
  orderId: string
): Promise<ShippingOrderDetail> => {
  const response = await fetch(`${API_URL}/orders/${encodeURIComponent(orderId)}?permissions=false`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch order");
  }
  return (result?.data || result) as ShippingOrderDetail;
};

/* ============================================================================
   EASYPOST SHIPMENTS BY ORDER ID & TRACKING API CALLS
   ============================================================================ */

export type OrderShipmentRecord = {
  _id: string;
  request_uuid?: string;
  shipment_id: string;
  rate_id: string;
  carrier: string;
  service?: string;
  postage_amount: string;
  currency: string;
  tracking_code: string;
  label_url: string;
  label_format?: string;
  label_layout?: string;
  insurance?: string;
  wallet_transaction_id?: string;
  raw?: Record<string, unknown>;
  created_by?: { id: string; name: string };
  updated_by?: { id: string; name: string };
  created_at: string;
  updated_at: string;
};

/**
 * Get Shipments by Order ID
 * GET /api/v1/easypost/orderid/:orderId
 * Returns array of shipment records (normalizes single object vs array).
 */
export const getShipmentsByOrderId = async (
  token: string,
  orderId: string
): Promise<OrderShipmentRecord[]> => {
  const response = await fetch(`${API_URL}/easypost/labels/by-order/${encodeURIComponent(orderId)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.error || result?.message || "Failed to fetch shipments by order");
  }

  const data = result?.data ?? result;
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return [data];
  return [];
};

export type TrackingDetail = {
  object?: string;
  message: string;
  description?: string;
  status?: string;
  status_detail?: string;
  datetime: string;
  source?: string;
  carrier_code?: string;
  tracking_location?: {
    object?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
  };
};

export type TrackingApiResponse = {
  id?: string;
  object?: string;
  mode?: string;
  tracking_code: string;
  status?: string;
  status_detail?: string;
  created_at?: string;
  updated_at?: string;
  signed_by?: string | null;
  weight?: number;
  est_delivery_date?: string | null;
  shipment_id?: string;
  carrier?: string;
  tracking_details: TrackingDetail[];
  carrier_detail?: Record<string, unknown>;
  finalized?: boolean;
  is_return?: boolean;
  public_url?: string;
  fees?: unknown[];
};

/**
 * Get Tracking by Tracking Code
 * GET /api/v1/easypost/tracking/:trackingCode
 * 404: tracking not found / access denied; 403: no permission; 400: tracking_code required.
 */
export const getTrackingByCode = async (
  token: string,
  trackingCode: string
): Promise<TrackingApiResponse> => {
  const code = String(trackingCode).trim();
  if (!code) {
    throw new Error("tracking_code is required");
  }

  const response = await fetch(
    `${API_URL}/easypost/tracking/${encodeURIComponent(code)}?permissions=false`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.error || result?.message || "Failed to fetch tracking");
  }

  return result?.data ?? result;
};

const formatTrackingEventDate = (d: string) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

/** Maps EasyPost-style tracking API payload to `TrackShipmentModal` data (same as Buy Labels UI). */
export function mapTrackingResponseToTrackData(raw: TrackingApiResponse | null | undefined): TrackData {
  const details = Array.isArray(raw?.tracking_details) ? raw.tracking_details : [];
  const estDate = raw?.est_delivery_date ?? null;
  return {
    estimatedDeliveryTitle: estDate ? formatTrackingEventDate(estDate) || String(estDate) : "—",
    estimatedDeliverySub: estDate ? String(estDate).slice(0, 10) : "—",
    events:
      details.length > 0
        ? details.map(
            (
              e: {
                message?: string;
                status_detail?: string;
                status?: string;
                datetime?: string;
              },
              i: number
            ) => ({
              title: String(e?.message ?? e?.status_detail ?? e?.status ?? "—"),
              dateLabel: e?.datetime ? formatTrackingEventDate(e.datetime) || e.datetime : "",
              isActive: i === 0,
            })
          )
        : [
            {
              title: String(raw?.status_detail ?? raw?.status ?? "No tracking details yet."),
              dateLabel: "",
              isActive: true,
            },
          ],
  };
}

/**
 * Public tracking (no auth) — GET /api/v1/public/easypost/tracking/:trackingCode
 */
export const getPublicTrackingByCode = async (trackingCode: string): Promise<TrackingApiResponse> => {
  const code = String(trackingCode).trim();
  if (!code) {
    throw new Error("tracking_code is required");
  }

  const response = await fetch(`${API_URL}/public/easypost/tracking/${encodeURIComponent(code)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.error || result?.message || "Failed to fetch tracking");
  }

  return result?.data ?? result;
};

/* ============================================================================
   EASYPOST LABELS (BUY LABELS LIST) API CALLS
   ============================================================================ */

export type EasyPostLabelRecord = {
  _id: string;
  request_uuid: string;
  shipment_id: string;
  rate_id: string;
  carrier: string;
  service: string;
  order_database_id?: string;
  order_id?: string;
  postage_amount: string;
  postage_amount_cents?: number;
  currency: string;
  tracking_code: string;
  label_url: string;
  label_format?: string;
  label_layout?: string;
  insurance?: string;
  wallet_transaction_id?: string;
  created_by?: { id: string; name: string };
  updated_by?: { id: string; name: string };
  created_at: string;
  updated_at: string;
};

export type EasyPostLabelsDatatableParams = {
  page: number;
  limit: number;
  search?: string;
  filters?: Record<string, any>;
  sort?: { field: string; order: "asc" | "desc" };
};

export type EasyPostLabelsDatatableData = {
  data: EasyPostLabelRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

/**
 * Labels DataTable
 * POST /api/v1/easypost/labels/datatable
 */
export const fetchEasyPostLabelsDatatable = async (
  token: string,
  params: EasyPostLabelsDatatableParams
): Promise<EasyPostLabelsDatatableData> => {
  const response = await fetch(`${API_URL}/easypost/labels/datatable`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page: params.page,
      limit: params.limit,
      search: params.search || "",
      filters: params.filters || {},
      sort: params.sort || { field: "created_at", order: "desc" },
    }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch labels");
  }

  // Expected format: { success: true, data: { data: [...], total, page, limit, totalPages } }
  if (result?.data?.data) return result.data;
  // Fallback if API returns the datatable directly
  return result;
};

export const fetchEasyPostLabelsDatatablesalesOrder = async (
  token: string,
  params: EasyPostLabelsDatatableParams
): Promise<EasyPostLabelsDatatableData> => {
  const response = await fetch(`${API_URL}/easypost/labels/datatable?permissions=false`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page: params.page,
      limit: params.limit,
      search: params.search || "",
      filters: params.filters || {},
      sort: params.sort || { field: "created_at", order: "desc" },
    }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result?.message || result?.error || "Failed to fetch labels");
  }

  // Expected format: { success: true, data: { data: [...], total, page, limit, totalPages } }
  if (result?.data?.data) return result.data;
  // Fallback if API returns the datatable directly
  return result;
};

/* ============================================================================
   EASYPOST LABEL DOWNLOAD (STREAM PDF)
   ============================================================================ */

function getFilenameFromContentDisposition(headerValue: string | null): string | null {
  if (!headerValue) return null;
  // examples:
  // attachment; filename="label_123.pdf"
  // attachment; filename=label_123.pdf
  const m = headerValue.match(/filename\*?=(?:UTF-8''|")?([^\";]+)"?/i);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

/**
 * Download Label (stream PDF)
 * GET /api/v1/easypost/labels/download/:label_id
 */
export const downloadEasyPostLabelPdf = async (
  token: string,
  labelId: string
): Promise<{ blob: Blob; filename: string }> => {
  const response = await fetch(`${API_URL}/easypost/labels/download/${labelId}?permissions=false`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // On errors backend returns json
  if (!response.ok) {
    const errJson = await response.json().catch(() => ({}));
    throw new Error(errJson?.error || errJson?.message || "Failed to download label");
  }

  const blob = await response.blob();
  const cd = response.headers.get("content-disposition");
  const filename = getFilenameFromContentDisposition(cd) || `label_${labelId}.pdf`;
  return { blob, filename };
};
